import type { JSX, Key } from 'react';
import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { Tab, Tabs } from '@heroui/react';

import { useTeamDetail } from '~/hooks/useTeams';
import { useTenant } from '~/hooks/useTenant';

import { LoadingSpinner } from '~/components/LoadingSpinner';

import { TeamMembers } from './TeamMembers';
import { TeamSettings } from './TeamSettings';

type TabKey = 'settings' | 'members';

export function OrganizationTeam(): JSX.Element {
  const { teamId } = useParams<{ teamId: string }>();
  const { loading: tenantLoading } = useTenant();
  const { error, isLoading, team } = useTeamDetail(teamId);
  const [selectedTab, setSelectedTab] = useState<TabKey>('members');

  const handleTabChange = (key: Key): void => {
    setSelectedTab(key as TabKey);
  };

  if (tenantLoading || isLoading) {
    return (
      <div className="flex items-center justify-center min-h-100">
        <LoadingSpinner />
      </div>
    );
  }

  if (error || !team) {
    return (
      <div className="text-center py-12">
        <p className="text-danger text-lg">Team not found</p>
        <p className="mt-2">
          The team you're looking for doesn't exist or you don't have access to it.
        </p>
      </div>
    );
  }

  return (
    <div>
      <div>
        <h2 className="text-xl font-bold mb-2">{team.name}</h2>
        {team.description && <p className="mb-6">{team.description}</p>}
      </div>

      {/* Tabs */}
      <Tabs
        aria-label="Team settings"
        selectedKey={selectedTab}
        onSelectionChange={handleTabChange}
        variant="underlined"
        classNames={{
          tabList: 'gap-6 w-full relative rounded-none p-0 border-b border-divider',
          cursor: 'w-full bg-primary',
          tab: 'max-w-fit px-0 h-12',
          tabContent: 'group-data-[selected=true]:text-primary',
        }}
      >
        <Tab key="members" title="Members">
          <TeamMembers team={team} />
        </Tab>
        <Tab key="settings" title="Settings">
          <TeamSettings team={team} />
        </Tab>
      </Tabs>
    </div>
  );
}
